/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { Row, Col } from 'react-flexbox-grid';
import { connect } from 'react-redux';
import { get } from 'lodash';
import Search from 'material-ui/svg-icons/action/search';
import DirectionsCar from 'material-ui/svg-icons/maps/directions-car';
import Security from 'material-ui/svg-icons/hardware/security';

import { compose, withState, withHandlers, mapProps, withProps, lifecycle } from 'recompose';
import { formValueSelector, change } from 'redux-form';
import { localLookupVehicle, externalLookupVehicle } from '@modules/wantedVehicles/_wantedVehiclesActions';
import { InputField, Input, RemoteAutoCompleteField, RemoteAutoComplete } from '@components/form';
import { ModalButton } from '@components/modalButton/ModalButton';
import { LocalLookupsForm } from '@components/vehicle/LocalLookupsForm';
import { ExternalLookupsForm } from '@components/vehicle/ExternalLookupsForm';
import { CrvLookupsForm } from '@components/vehicle/CrvLookupsForm';
import { withNotifications } from '@components/hoc/withNotifications';
import { getConfig } from '@components/provider/configuration/_configurationActions';
import { PcrVehicleForm } from './PcrVehicleForm';
import { columnsDefinition, InputType } from './columns';

export const enhance = compose<any, any>(
  withState('localLookups', 'handleLocalLookups', []),
  withState('externalLookups', 'handleExternalLookups', null),
  withState('crvLookupsVersion', 'handleCrvLookupsVersion', 0),
  withNotifications({ getConfig }),
  mapProps(({ formName, ...rest }) => ({ formName: formName ? `${formName}.` : '', ...rest })),
  connect<any>(
    (state, { form, formName }: any) => ({
      registrationNumber: formValueSelector(form)(state, `${formName}registrationNumber`),
      vinNumber: formValueSelector(form)(state, `${formName}vinNumber`),
      certificateNumber: formValueSelector(form)(state, `${formName}registrationCertificate`),
      owner: formValueSelector(form)(state, `${formName}owner`),
      type: formValueSelector(form)(state, `${formName}type`),
      brand: formValueSelector(form)(state, `${formName}brand`),
      color: formValueSelector(form)(state, `${formName}color`)
    }),
    (dispatch, { form, formName }: any) => ({
      changeFieldValue: (label, value) => dispatch(change(form, `${formName}${label}`, value))
    })
  ),
  lifecycle<any, {}, {}>({
    async componentDidMount() {
      const { getConfigNotify } = this.props;
      try {
        const config = await getConfigNotify();
        this.setState({ config });
      } catch {
        this.setState({ config: {} });
      }
    }
  }),
  withProps<any, any>(({ registrationNumber, vinNumber, certificateNumber, config, type, brand, color }) => ({
    initialValuesCrv: {
      registrationNumber,
      certificateNumber,
      vinNumber
    },
    initialValuesPcr: {
      registrationNumber,
      vinNumber,
      type,
      brand,
      color
    },
    isPcrIszr: get(config, 'extensions.pcr-iszr') === 'true'
  })),
  mapProps<any, any>(({ owner = {}, ...rest }) => {
    const { firstName, lastName } = owner;
    const fullName = firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName;
    return { owner: fullName, ...rest };
  }),
  withHandlers<any, any>({
    incrementCrvLookupsVersion: ({ crvLookupsVersion, handleCrvLookupsVersion }) => () => {
      handleCrvLookupsVersion(crvLookupsVersion + 1);
    },
    searchLocalLookups: ({ registrationNumber, vinNumber, owner, handleLocalLookups }) => async () => {
      const response = await localLookupVehicle({ registrationNumber, vinNumber, owner });
      handleLocalLookups(response);
    },
    searchExternalLookups: ({ registrationNumber, vinNumber, handleExternalLookups }) => async () => {
      const response = await externalLookupVehicle({ registrationNumber, vinNumber });
      handleExternalLookups(response);
    }
  })
);

const Vehicle: React.SFC<any> = ({
  searchLocalLookups,
  searchExternalLookups,
  localLookups,
  externalLookups,
  initialValuesCrv,
  initialValuesPcr,
  changeFieldValue,
  incrementCrvLookupsVersion,
  crvLookupsVersion,
  isPcrIszr,
  ...rest
}) => {
  return (
    <React.Fragment>
      <Row>
        {[
          {
            label: 'Místní pátrání',
            icon: <Search />,
            ModalComponent: LocalLookupsForm,
            onClick: searchLocalLookups,
            items: localLookups
          },
          {
            label: 'Lustrace CRV',
            icon: <DirectionsCar />,
            ModalComponent: CrvLookupsForm,
            initialValues: initialValuesCrv,
            changeFieldValue,
            incrementCrvLookupsVersion
          }
        ].map((item, key) => <ModalButton key={key} style={{ marginBottom: 10, marginRight: 10 }} {...item} />)}
        {isPcrIszr ? (
          <ModalButton
            {...{
              label: 'Pátrání PČR',
              icon: <Security />,
              style: { marginBottom: 10, marginRight: 10 },
              ModalComponent: PcrVehicleForm,
              initialValues: initialValuesPcr,
              changeFieldValue,
              dialogStyles: {
                style: { paddingTop: '5vh' },
                contentStyle: {
                  paddingTop: 5,
                  minWidth: '85vw',
                  transform: 'none'
                },
                bodyStyle: { padding: '0', overflowY: 'auto', maxHeight: '90vh' },
                autoDetectWindowHeight: false
              }
            }}
          />
        ) : (
          <ModalButton
            {...{
              label: 'Pátrání PČR',
              icon: <Security />,
              ModalComponent: ExternalLookupsForm,
              onClick: searchExternalLookups,
              vehicle: externalLookups
            }}
          />
        )}
      </Row>
      <Row>
        {columnsDefinition.map(({ type, name, label, url, filter }) => {
          const constructFilter = (filterField: string) => {
            const value = get(rest, filterField);
            return value ? [{ field: filterField, operation: 'EQ', value: get(value, 'id') }] : [];
          };

          return (
            <Col md={4} key={name}>
              {type === InputType.INPUT && <InputField component={Input} id={name} name={name} label={label} />}
              {type === InputType.REMOTE_AUTOCOMPLETE && (
                <RemoteAutoCompleteField
                  component={RemoteAutoComplete}
                  id={name}
                  name={name}
                  label={label}
                  url={url}
                  filterActive={true}
                  fullWidth={true}
                  labelFunction={o => get(o, 'name')}
                  version={crvLookupsVersion}
                  disabled={!!filter && !get(rest, filter)}
                  {...filter && { params: { filter: constructFilter(filter) } }}
                />
              )}
            </Col>
          );
        })}
      </Row>
    </React.Fragment>
  );
};

export default enhance(Vehicle);



// WEBPACK FOOTER //
// ./src/components/vehicle/Vehicle.tsx